import React, { useState, useEffect } from "react";
import axios from "axios";
import { AddIcon } from "../../assets/icons/icons";
import CustomAlert from "../CustomAlert";
import { useNavigate, useParams } from "react-router-dom";

/* ===== ثابتات ===== */
const capacityList = ["8 GB", "16 GB", "32 GB", "64 GB", "128 GB", "256 GB", "512 GB", "1 T"];

const API_BASE = "https://laravel-api.aj-mart.com/api";
const STORAGE_BASE = "https://laravel-api.aj-mart.com/storage/app/public";

/* ===== Helpers ===== */
const tryParseJSON = (v) => {
  if (typeof v !== "string") return v;
  const s = v.trim();
  if (!(s.startsWith("[") || s.startsWith("{"))) return v;
  try { return JSON.parse(s); } catch { return v; }
};
const toList = (v) => {
  const parsed = tryParseJSON(v);
  if (Array.isArray(parsed)) return parsed;
  if (typeof parsed === "string") {
    const s = parsed.trim();
    if (!s) return [];
    if (s.includes(",")) return s.split(",").map(x => x.trim()).filter(Boolean);
    return [s];
  }
  return [];
};
const normalizeCapKey = (s) => String(s || "").toLowerCase().replace(/\s+/g, "");
const normalize = (s) => (s || "").toLowerCase().replace(/&/g, "and").replace(/\s+/g, "");
const SHOW_CAPACITY_CATEGORIES = new Set(["tabletandipad","smartphones","smartwatches","smartwathes","laptop","laptops"]);
const shouldShowCapacity = (cat) => SHOW_CAPACITY_CATEGORIES.has(normalize(cat));
const parseCapPrices = (input) => {
  if (!input) return {};
  const val = tryParseJSON(input);
  return val && typeof val === "object" ? val : {};
};
const num = (v) => {
  const n = Number.parseFloat(String(v ?? "").toString().replace(",", "."));
  return Number.isFinite(n) ? n : null;
};
/* تعقيم كود اللون (#RGB/#RRGGBB) */
const sanitizeCssColor = (val) => {
  if (!val || typeof val !== "string") return "";
  const s = val.trim();
  const hex = s.replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(hex) || /^[0-9a-f]{6}$/i.test(hex)) return `#${hex}`;
  return "";
};
/* ==================== */

export default function EditProducts() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    product_id: "",
    product_name_E: "",
    product_name_A: "",
    product_price: "",
    product_tag: "",
    product_state: "New",
    product_category: "",
    product_sub_category: "",
    product_brand_name: "",
    product_price_discount: "",
    product_stock_available: "",
    product_desc_E: "",
    product_desc_A: "",
    product_features_E: "",
    product_features_A: "",
    product_color: [],      // أسماء الألوان فقط (زي AddProduct)
    product_capacity: [],
    product_status: "New",
  });

  /* capacity prices UI state (cap -> price) */
  const [capacityPrices, setCapacityPrices] = useState({});

  const [mainImage, setMainImage] = useState(null);
  const [mainPreview, setMainPreview] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [additionalPreviews, setAdditionalPreviews] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertMsg, setAlertMsg] = useState({ type: "", message: "" });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  /* ===== ألوان ديناميكية (مثل AddProduct) ===== */
  const [colorName, setColorName] = useState("");
  const [colorHex, setColorHex] = useState("#000000");
  // مصفوفة كائنات: [{ name, hex }]
  const [colorsWithHex, setColorsWithHex] = useState([]);

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }, []);

  /* pickers */
  useEffect(() => {
    axios.get(`${API_BASE}/categories`)
      .then(res => setCategories(res.data?.category || []))
      .catch(err => console.error("Error fetching categories:", err));

    axios.get(`${API_BASE}/brand`)
      .then(res => setBrands(res.data?.brands || []))
      .catch(err => console.error("Error fetching brands:", err));

    axios.get(`${API_BASE}/subCategory`)
      .then(res => setSubCategories(res.data?.sub_category || []))
      .catch(err => console.error("Error fetching subcategories:", err));
  }, []);

  /* fetch product + hydrate form (وتحضير الألوان الديناميكية) */
  useEffect(() => {
    axios.get(`${API_BASE}/products/${id}`)
      .then((response) => {
        const product = response.data?.data ?? response.data?.product ?? response.data;
        if (!product || !product.product_id) throw new Error("Product data not found");

        // ألوان (أسماء)
        const colorsArr = toList(product.product_color ?? product.product_color_array);

        // ألوان مع HEX (لو السيرفر حافظها)
        let parsedColorsWithHex = [];
        const rawColorJson =
          product.product_colors_with_hex ??
          product.product_colors_hex ??
          product.colors_with_hex ??
          null;

        const parsed = tryParseJSON(rawColorJson);
        if (Array.isArray(parsed)) {
          // شكل: [{name, hex}, ...]
          parsedColorsWithHex = parsed
            .filter((c) => c && typeof c === "object" && c.name)
            .map((c) => ({ name: String(c.name), hex: sanitizeCssColor(c.hex || "") }));
        } else if (parsed && typeof parsed === "object") {
          // شكل: { "Red": "#ff0000", "Blue": "#0000ff" }
          parsedColorsWithHex = Object.entries(parsed).map(([name, hex]) => ({
            name: String(name),
            hex: sanitizeCssColor(String(hex || "")),
          }));
        }

        // نضمن وجود عنصر لكل اسم لون موجود
        const mergedColorsWithHex = (colorsArr || []).map((name) => {
          const found = parsedColorsWithHex.find(
            (c) => c.name.toLowerCase() === String(name).toLowerCase()
          );
          return { name, hex: sanitizeCssColor(found?.hex || "") };
        });

        const capsArr = toList(product.product_capacity ?? product.product_capacity_array);
        const imgsArr = toList(product.product_images ?? product.product_images_array);
        const capMap = parseCapPrices(product.product_capacity_prices);

        // build capacityPrices aligned with selected capacities
        const initCapPrices = {};
        capsArr.forEach((cap) => {
          let matched = "";
          for (const [k, v] of Object.entries(capMap)) {
            if (normalizeCapKey(k) === normalizeCapKey(cap)) {
              matched = String(v ?? "");
              break;
            }
          }
          initCapPrices[cap] = matched;
        });

        setFormData({
          product_id: product.product_id,
          product_name_E: product.product_name_E ?? "",
          product_name_A: product.product_name_A ?? "",
          product_price: product.product_price ?? "",
          product_tag: product.product_tag ?? "",
          product_state: product.product_state ?? "New",
          product_category: product.product_category ?? "",
          product_sub_category: product.product_sub_category ?? "",
          product_brand_name: product.product_brand_name ?? "",
          product_price_discount: product.product_price_discount ?? "",
          product_stock_available: product.product_stock_available ?? "",
          product_desc_E: product.product_desc_E ?? "",
          product_desc_A: product.product_desc_A ?? "",
          product_features_E: product.product_features_E ?? "",
          product_features_A: product.product_features_A ?? "",
          product_color: colorsArr,
          product_capacity: capsArr,
          product_status: product.product_status ?? "New",
        });

        setColorsWithHex(mergedColorsWithHex);
        setCapacityPrices(initCapPrices);

        if (product.product_main_img) {
          setMainPreview(`${STORAGE_BASE}/${product.product_main_img}`);
        }
        if (imgsArr.length) {
          const fullUrls = imgsArr.map((img) =>
            String(img || "").startsWith("http") ? img : `${STORAGE_BASE}/${img}`
          );
          setAdditionalPreviews(fullUrls);
        }
      })
      .catch((error) => {
        console.error("Error fetching product:", error);
        setAlertMsg({ type: "error", message: "Failed to fetch product data" });
      });
  }, [id]);

  /* images */
  const handleMainImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setMainImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setMainPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };
  const handleAdditionalImagesChange = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 10);
    setAdditionalImages(files);
    const previews = files.map((file) => {
      const r = new FileReader();
      return new Promise((resolve) => {
        r.onloadend = () => resolve(r.result);
        r.readAsDataURL(file);
      });
    });
    Promise.all(previews).then((res) => setAdditionalPreviews(res));
  };

  /* inputs */
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((p) => {
      const next = { ...p, [name]: value };
      if (name === "product_category" && !shouldShowCapacity(value)) {
        // clear capacities if category doesn't support it
        next.product_capacity = [];
        setCapacityPrices({});
      }
      return next;
    });
  };

  /* checkbox (للسعات فقط الآن) */
  const handleCheckboxChange = (e, field) => {
    const value = e.target.value;
    setFormData((p) => {
      const exists = p[field].includes(value);
      const nextArr = exists ? p[field].filter((v) => v !== value) : [...p[field], value];

      if (field === "product_capacity") {
        setCapacityPrices((old) => {
          const clone = { ...old };
          if (!exists) {
            if (clone[value] == null) clone[value] = "";
          } else {
            delete clone[value];
          }
          Object.keys(clone).forEach((k) => {
            if (!nextArr.includes(k)) delete clone[k];
          });
          return clone;
        });
      }

      return { ...p, [field]: nextArr };
    });
  };

  const handleCapacityPriceChange = (cap, v) => {
    setCapacityPrices((p) => ({ ...p, [cap]: v }));
  };

  /* ====== منطق الألوان الديناميكية (نفس AddProduct) ====== */
  const addColor = () => {
    const name = colorName.trim();
    if (!name) return;

    // لو الاسم موجود، لا نضيفه مرتين؛ بدلاً من ذلك نحدّث HEX لو وُجد
    const existsByName = formData.product_color.some(
      (c) => c.toLowerCase() === name.toLowerCase()
    );

    const hex = sanitizeCssColor(colorHex) || "";

    if (existsByName) {
      setColorsWithHex((prev) =>
        prev.map((c) =>
          c.name.toLowerCase() === name.toLowerCase() ? { ...c, hex } : c
        )
      );
    } else {
      setFormData((p) => ({ ...p, product_color: [...p.product_color, name] }));
      setColorsWithHex((prev) => [...prev, { name, hex }]);
    }

    setColorName("");
    // نخلي اختيار الـ color كما هو لتسهيل الإضافات السريعة
  };

  const removeColor = (name) => {
    setFormData((p) => ({
      ...p,
      product_color: p.product_color.filter((c) => c.toLowerCase() !== name.toLowerCase()),
    }));
    setColorsWithHex((prev) =>
      prev.filter((c) => c.name.toLowerCase() !== name.toLowerCase())
    );
  };

  const updateColorHex = (name, hexVal) => {
    const sanitized = sanitizeCssColor(hexVal) || "";
    setColorsWithHex((prev) =>
      prev.map((c) =>
        c.name.toLowerCase() === name.toLowerCase() ? { ...c, hex: sanitized } : c
      )
    );
  };

  const handleColorNameKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addColor();
    }
  };

  /* submit */
  const handleSubmit = (e) => {
    e.preventDefault();
    setShowConfirmDialog(true);
  };

  const confirmUpdate = async () => {
    setShowConfirmDialog(false);
    setIsSubmitting(true);

    const fd = new FormData();

    // append simple fields (بدون product_username)
    const simpleKeys = [
      "product_id","product_name_E","product_name_A","product_price","product_tag","product_state",
      "product_category","product_sub_category","product_brand_name","product_price_discount",
      "product_stock_available","product_desc_E","product_desc_A","product_features_E",
      "product_features_A","product_status"
    ];
    simpleKeys.forEach((k) => fd.append(k, formData[k] ?? ""));

    // colors names
    (formData.product_color || []).forEach((c) => fd.append("product_color[]", c));

    // colors with hex as JSON (زي AddProduct)
    const jsonColors = (formData.product_color || []).map((name) => {
      const found = colorsWithHex.find((c) => c.name.toLowerCase() === name.toLowerCase());
      return { name, hex: sanitizeCssColor(found?.hex || "") };
    });
    fd.append("product_colors_with_hex", JSON.stringify(jsonColors));

    // capacities
    (formData.product_capacity || []).forEach((cap) => fd.append("product_capacity[]", cap));

    // capacity prices map
    Object.entries(capacityPrices || {}).forEach(([cap, price]) => {
      if (price !== "" && price != null) {
        fd.append(`product_capacity_prices[${cap}]`, String(price));
      }
    });

    // إذا base price فاضي وفي أسعار سعات مدخلة -> خذ أقل سعر كسعر أساسي
    const capNums = Object.values(capacityPrices)
      .map((v) => num(v))
      .filter((n) => n != null && n >= 0);
    if ((!formData.product_price || !num(formData.product_price)) && capNums.length) {
      fd.set("product_price", String(Math.min(...capNums)));
    }

    // images
    if (mainImage) fd.append("product_main_img", mainImage);
    (additionalImages || []).forEach((img) => fd.append("product_images[]", img));

    try {
      const res = await axios.post(
        `${API_BASE}/products/update/${formData.product_id}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const data = res.data || {};
      if ((res.status === 200 || res.status === 201) && (data.success || data.status)) {
        setAlertMsg({ type: "success", message: data.message || "Product updated successfully" });
        setTimeout(() => navigate("/AllProducts"), 1200);
      } else {
        setAlertMsg({ type: "error", message: data.message || "Failed to update product" });
      }
    } catch (error) {
      console.error("Error updating product:", error);
      setAlertMsg({
        type: "error",
        message: error.response?.data?.message || "An unexpected error occurred",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const showCapacity = shouldShowCapacity(formData.product_category);

  return (
    <section className="py-[25px] flex flex-col gap-[50px]">
      <div className="main-title">Edit Product</div>

      {alertMsg.message && (
        <CustomAlert
          type={alertMsg.type}
          message={alertMsg.message}
          onClose={() => setAlertMsg({ type: "", message: "" })}
        />
      )}

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <div className="flex flex-col gap-[15px]">
          <label className="text-black capitalize">Product Name In English</label>
          <input
            type="text"
            name="product_name_E"
            value={formData.product_name_E}
            onChange={handleChange}
            placeholder="Product Name In English"
            className="px-[15px] py-[7px] border-2 rounded-[4px] text-black bg-[#eeeeee6e]"
            required
          />

          <label className="text-black capitalize">Product Name In Arabic</label>
          <input
            type="text"
            name="product_name_A"
            value={formData.product_name_A}
            onChange={handleChange}
            placeholder="Product Name In Arabic"
            className="px-[15px] py-[7px] border-2 rounded-[4px] text-black bg-[#eeeeee6e]"
            required
          />

          <label className="text-black capitalize">Product Price</label>
          <input
            type="number"
            name="product_price"
            value={formData.product_price}
            onChange={handleChange}
            placeholder="Product Price"
            className="px-[15px] py-[7px] border-2 rounded-[4px] text-black bg-[#eeeeee6e]"
          />

          <label className="text-black capitalize">Tags</label>
          <input
            type="text"
            name="product_tag"
            value={formData.product_tag}
            onChange={handleChange}
            placeholder="Tags"
            className="px-[15px] py-[7px] border-2 rounded-[4px] text-black bg-[#eeeeee6e]"
          />
        </div>

        {/* Category, Sub Category, Brand, and State */}
        <div className="flex flex-col gap-[15px] mt-4">
          <label className="text-black capitalize">Product Category</label>
          <select
            name="product_category"
            value={formData.product_category}
            onChange={handleChange}
            className="px-[15px] py-[7px] border-2 rounded-[4px] text-black bg-[#eeeeee6e]"
            required
          >
            <option value="">Select Product Category</option>
            {categories.length ? (
              categories.map((cat) => (
                <option key={cat.category_id} value={cat.category_name_E}>
                  {cat.category_name_E}
                </option>
              ))
            ) : (
              <option value="">No categories available</option>
            )}
          </select>

          <label className="text-black capitalize">Product Sub Category</label>
          <select
            name="product_sub_category"
            value={formData.product_sub_category}
            onChange={handleChange}
            className="px-[15px] py-[7px] border-2 rounded-[4px] text-black bg-[#eeeeee6e]"
            required
          >
            <option value="">Select Product Sub Category</option>
            {subCategories.length ? (
              subCategories.map((sub) => (
                <option key={sub.sub_category_id} value={sub.sub_category_name_E}>
                  {sub.sub_category_name_E}
                </option>
              ))
            ) : (
              <option value="">No sub categories available</option>
            )}
          </select>

          <label className="text-black capitalize">Brand Name</label>
          <select
            name="product_brand_name"
            value={formData.product_brand_name}
            onChange={handleChange}
            className="px-[15px] py-[7px] border-2 rounded-[4px] text-black bg-[#eeeeee6e]"
            required
          >
            <option value="">Select Brand Name</option>
            {brands.length ? (
              brands.map((brand) => (
                <option key={brand.brand_id} value={brand.brand_name}>
                  {brand.brand_name}
                </option>
              ))
            ) : (
              <option value="">No brands available</option>
            )}
          </select>

          <label className="text-black capitalize">Product State</label>
          <select
            name="product_state"
            value={formData.product_state}
            onChange={handleChange}
            className="px-[15px] py-[7px] border-2 rounded-[4px] text-black bg-[#eeeeee6e]"
            required
          >
            <option value="New">New</option>
            <option value="Used">Used</option>
          </select>
        </div>

        {/* Discount and Stock */}
        <div className="flex flex-col gap-[15px] mt-4">
          <label className="text-black capitalize">Discount (%)</label>
          <input
            type="number"
            name="product_price_discount"
            value={formData.product_price_discount}
            onChange={handleChange}
            placeholder="Discount Percentage"
            className="px-[15px] py-[7px] border-2 rounded-[4px] text-black bg-[#eeeeee6e]"
          />
          <label className="text-black capitalize">Stock Available</label>
          <input
            type="number"
            name="product_stock_available"
            value={formData.product_stock_available}
            onChange={handleChange}
            placeholder="Stock Available"
            className="px-[15px] py-[7px] border-2 rounded-[4px] text-black bg-[#eeeeee6e]"
            required
          />
        </div>

        {/* Descriptions and Features */}
        <div className="flex flex-col gap-[15px] mt-4">
          <label className="text-black capitalize">Product Description In English</label>
          <textarea
            name="product_desc_E"
            value={formData.product_desc_E}
            onChange={handleChange}
            placeholder="Product Description In English"
            className="px-[15px] py-[7px] border-2 rounded-[4px] text-black bg-[#eeeeee6e] h-[150px]"
          />
          <label className="text-black capitalize">Product Description In Arabic</label>
          <textarea
            name="product_desc_A"
            value={formData.product_desc_A}
            onChange={handleChange}
            placeholder="Product Description In Arabic"
            className="px-[15px] py-[7px] border-2 rounded-[4px] text-black bg-[#eeeeee6e] h-[150px]"
          />
          <label className="text-black capitalize">Product Features In English</label>
          <textarea
            name="product_features_E"
            value={formData.product_features_E}
            onChange={handleChange}
            placeholder="Product Features In English"
            className="px-[15px] py-[7px] border-2 rounded-[4px] text-black bg-[#eeeeee6e] h-[150px]"
          />
          <label className="text-black capitalize">Product Features In Arabic</label>
          <textarea
            name="product_features_A"
            value={formData.product_features_A}
            onChange={handleChange}
            placeholder="Product Features In Arabic"
            className="px-[15px] py-[7px] border-2 rounded-[4px] text-black bg-[#eeeeee6e] h-[150px]"
          />
        </div>

        {/* Colors (Dynamic) + Capacity */}
        <div className="flex flex-col gap-[15px] mt-4">
          {/* Colors (dynamic like AddProduct) */}
          <label className="text-black capitalize">Colors</label>
          <div className="flex flex-col gap-3 p-4 bg-gray-100 border-2 rounded border-[#1485a6]">
            <div className="flex flex-wrap gap-3 items-center">
              <input
                type="text"
                placeholder="Color name (e.g. Midnight Blue)"
                value={colorName}
                onChange={(e) => setColorName(e.target.value)}
                onKeyDown={handleColorNameKeyDown}
                className="flex-1 min-w-[220px] px-3 py-2 border rounded"
              />
              <input
                type="color"
                title="Color HEX (optional)"
                value={colorHex}
                onChange={(e) => setColorHex(e.target.value)}
                className="w-[48px] h-[40px] border rounded cursor-pointer"
              />
              <button
                type="button"
                onClick={addColor}
                className="px-3 py-2 bg-[#1485a6] text-white rounded hover:opacity-90"
              >
                Add Color
              </button>
            </div>

            {/* color chips */}
            {formData.product_color.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {formData.product_color.map((name) => {
                  const obj =
                    colorsWithHex.find(
                      (c) => c.name.toLowerCase() === name.toLowerCase()
                    ) || { name, hex: "" };
                  return (
                    <div
                      key={name}
                      className="flex items-center gap-2 bg-white border rounded-full px-3 py-1 shadow-sm"
                    >
                      <span
                        className="inline-block w-4 h-4 rounded-full border"
                        style={{ backgroundColor: obj.hex || "transparent" }}
                        title={obj.hex || "No HEX"}
                      />
                      <span className="text-sm font-medium">{name}</span>
                      <input
                        type="color"
                        value={obj.hex || "#000000"}
                        onChange={(e) => updateColorHex(name, e.target.value)}
                        className="w-[32px] h-[24px] border rounded cursor-pointer"
                        title="Change HEX"
                      />
                      <button
                        type="button"
                        onClick={() => removeColor(name)}
                        className="text-red-600 hover:text-red-700 text-sm font-semibold"
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No colors added yet.</p>
            )}
          </div>

          {/* Capacity + Prices */}
          {showCapacity && (
            <div className="flex flex-col gap-2">
              <label className="text-black capitalize">Select Capacity</label>
              <div className="p-4 bg-gray-100 border-2 rounded-[4px] border-[#1485a6]">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {capacityList.map((cap, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`capacity-${idx}`}
                        name="product_capacity"
                        className="w-5 h-5 accent-blue-500 border-gray-300 rounded"
                        value={cap}
                        checked={formData.product_capacity.includes(cap)}
                        onChange={(e) => handleCheckboxChange(e, "product_capacity")}
                      />
                      <label htmlFor={`capacity-${idx}`} className="text-gray-700 capitalize cursor-pointer">
                        {cap}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Per-capacity prices */}
              {formData.product_capacity.length > 0 && (
                <div className="mt-3 p-4 bg-white border rounded">
                  <div className="text-sm font-semibold mb-2">Per-capacity price</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {formData.product_capacity.map((cap) => (
                      <div key={cap} className="flex items-center gap-2">
                        <label className="min-w-[90px] text-sm">{cap}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={capacityPrices[cap] ?? ""}
                          onChange={(e) => handleCapacityPriceChange(cap, e.target.value)}
                          className="flex-1 px-3 py-2 border rounded"
                          placeholder="Override price (e.g. 1350)"
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    إذا تركت سعر سعة فارغ، يبقى السعر الأساسي كما هو. إذا أدخلت أسعار للسعات وتركْت
                    السعر الأساسي فارغ، سيتم استخدام أقل سعر كسعر أساسي تلقائيًا عند الحفظ.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Images Section */}
        <div className="flex gap-[10px] items-start w-full flex-wrap md:flex-nowrap mt-4">
          <div className="flex flex-col items-center w-full md:w-1/2">
            <label
              htmlFor="main-image-upload"
              className="cursor-pointer w-full h-[350px] border-2 border-dashed border-[#1485a6] bg-[#eeeeee6e] text-black rounded flex items-center justify-center"
            >
              {mainPreview ? (
                <img src={mainPreview} alt="Main Preview" className="w-full h-full object-cover rounded" />
              ) : (
                "Main Image"
              )}
            </label>
            <input
              id="main-image-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleMainImageChange}
            />
          </div>

          <div className="flex flex-col items-center w-full md:w-1/2">
            <label
              htmlFor="additional-images-upload"
              className="cursor-pointer w-full h-[350px] border-2 border-dashed border-[#1485a6] bg-[#eeeeee6e] text-black rounded flex items-center justify-center"
            >
              {additionalPreviews.length > 0 ? (
                <div className="grid grid-cols-2 gap-2 w-full h-full p-2 overflow-y-auto">
                  {additionalPreviews.map((preview, index) => (
                    <img key={index} src={preview} alt={`Preview ${index + 1}`} className="w-full h-[80px] object-cover rounded" />
                  ))}
                </div>
              ) : (
                "Upload Additional Images"
              )}
            </label>
            <input
              id="additional-images-upload"
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleAdditionalImagesChange}
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex items-center gap-[10px] mt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn flex items-center gap-[5px] w-full justify-center bg-[#1485a6] text-white px-4 py-2 rounded hover:bg-[#106c89] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <AddIcon />
            <span>{isSubmitting ? "Saving..." : "Save Changes"}</span>
          </button>
        </div>
      </form>

      {/* Confirm Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white w-[400px] rounded-lg shadow-lg p-5 text-center">
            <h2 className="text-xl font-bold mb-4">Confirm Update</h2>
            <p className="mb-4">Are you sure you want to update the product?</p>
            <div className="flex justify-center gap-4">
              <button
                className="btn bg-gray-300 hover:bg-gray-400"
                onClick={() => setShowConfirmDialog(false)}
              >
                Cancel
              </button>
              <button className="btn bg-green-500 hover:bg-green-600" onClick={confirmUpdate}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
